/**
 * Notification Context
 * Provides global notification state with WebSocket real-time updates
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import logger from '../utils/logger';
import { useAuth0 } from '@auth0/auth0-react';
import { useWebSocket } from '../hooks/useWebSocket';
import * as notificationService from '../services/notificationService';
import { setAccessToken } from '../services/api';

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider'
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const lastFetchRef = useRef(0);
  const isFetchingRef = useRef(false);

  // Handle incoming WebSocket notification
  const handleNewNotification = useCallback((notification) => {
    // Add to the beginning of the list
    setNotifications((prev) => {
      // Avoid duplicates
      if (prev.some((n) => n.id === notification.id)) {
        return prev;
      }
      return [notification, ...prev];
    });

    // Increment unread count if not already read
    if (!notification.isRead) {
      setUnreadCount((prev) => prev + 1);
    }

    // Show browser notification if supported and permitted
    if (Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/brand/icon.png',
        tag: notification.id,
      });
    }
  }, []);

  // WebSocket connection
  const { isConnected, connectionError } = useWebSocket({
    onNotification: handleNewNotification,
    enabled: isAuthenticated,
    onConnect: () => {
      // Refresh notifications on reconnect
      fetchUnreadCount();
    },
    onDisconnect: () => {},
  });

  // Fetch unread count with debounce protection
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;

    // Debounce: prevent calls more frequent than 2 seconds
    const now = Date.now();
    if (now - lastFetchRef.current < 2000) {
      return;
    }

    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      return;
    }

    lastFetchRef.current = now;
    isFetchingRef.current = true;

    try {
      // Get and set the access token before making API call
      const token = await getAccessTokenSilently();
      setAccessToken(token);

      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (err) {
      // Only log once, not spam console
      logger.warn('Failed to fetch unread count:', err.message);
    } finally {
      isFetchingRef.current = false;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Fetch notifications
  const fetchNotifications = useCallback(
    async (reset = false) => {
      if (!isAuthenticated || isLoading) return;

      const pageToFetch = reset ? 0 : page;

      try {
        setIsLoading(true);
        setError(null);

        // Get and set the access token before making API call
        const token = await getAccessTokenSilently();
        setAccessToken(token);

        const response = await notificationService.getNotifications(
          pageToFetch,
          20
        );

        const content = Array.isArray(response?.content) ? response.content : [];

        if (reset) {
          setNotifications(content);
          setPage(1);
        } else {
          setNotifications((prev) => [...prev, ...content]);
          setPage((p) => p + 1);
        }

        setHasMore(response?.last === false);
      } catch (err) {
        logger.error('Failed to fetch notifications:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, isLoading, page, getAccessTokenSilently]
  );

  // Load more notifications
  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      fetchNotifications(false);
    }
  }, [hasMore, isLoading, fetchNotifications]);

  // Refresh notifications
  const refresh = useCallback(() => {
    fetchNotifications(true);
    fetchUnreadCount();
  }, [fetchNotifications, fetchUnreadCount]);

  // Mark notification as read
  const markAsRead = useCallback(
    async (id) => {
      try {
        // Get and set the access token before making API call
        const token = await getAccessTokenSilently();
        setAccessToken(token);

        await notificationService.markAsRead(id);

        setNotifications((prev) =>
          prev.map((n) =>
            n.id === id
              ? { ...n, isRead: true, readAt: new Date().toISOString() }
              : n
          )
        );

        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        logger.error('Failed to mark notification as read:', err);
      }
    },
    [getAccessTokenSilently]
  );

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      // Get and set the access token before making API call
      const token = await getAccessTokenSilently();
      setAccessToken(token);

      await notificationService.markAllAsRead();

      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          isRead: true,
          readAt: n.readAt || new Date().toISOString(),
        }))
      );

      setUnreadCount(0);
    } catch (err) {
      logger.error('Failed to mark all as read:', err);
    }
  }, [getAccessTokenSilently]);

  // Delete notification
  const deleteNotification = useCallback(
    async (id) => {
      try {
        // Get and set the access token before making API call
        const token = await getAccessTokenSilently();
        setAccessToken(token);

        await notificationService.deleteNotification(id);

        setNotifications((prev) => {
          const notification = prev.find((n) => n.id === id);
          if (notification && !notification.isRead) {
            setUnreadCount((count) => Math.max(0, count - 1));
          }
          return prev.filter((n) => n.id !== id);
        });
      } catch (err) {
        logger.error('Failed to delete notification:', err);
      }
    },
    [getAccessTokenSilently]
  );

  // Request browser notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  }, []);

  // Initial fetch on auth
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications(true);
      fetchUnreadCount();
    } else {
      setNotifications([]);
      setUnreadCount(0);
      setPage(0);
      setHasMore(true);
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Periodic unread count refresh
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 60000); // Every minute

    return () => clearInterval(interval);
  }, [isAuthenticated, fetchUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    isLoading,
    error,
    hasMore,
    isConnected,
    connectionError,
    fetchNotifications,
    loadMore,
    refresh,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    requestNotificationPermission,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
